import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Search, Edit, Package, Save, AlertTriangle, 
  Calendar, TrendingDown, History, RefreshCw, Upload, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const InventarioPage = () => {
  const [inventario, setInventario] = useState([]);
  const [alertas, setAlertas] = useState({ stock_bajo: [], por_vencer: [], vencidos: [] });
  const [resumen, setResumen] = useState([]);
  const [vacunas, setVacunas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    vacuna: '',
    lote: '',
    cantidad: 0,
    fecha_vencimiento: '',
    stock_minimo: 10,
    precio_unitario: 0,
    proveedor: '',
    fecha_ingreso: new Date().toISOString().split('T')[0],
    notas: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, alertasRes, resumenRes, catRes] = await Promise.all([
        axios.get(`${API}/inventario`, { withCredentials: true }),
        axios.get(`${API}/inventario/alertas`, { withCredentials: true }),
        axios.get(`${API}/inventario/resumen`, { withCredentials: true }),
        axios.get(`${API}/catalogs?category=vacuna`, { withCredentials: true })
      ]);
      
      setInventario(invRes.data);
      setAlertas(alertasRes.data);
      setResumen(resumenRes.data);
      setVacunas(catRes.data.map(c => c.value));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar el inventario');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Import Excel file
  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/inventario/importar-excel`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(`Importación completada: ${response.data.imported} nuevos, ${response.data.updated} actualizados`);
      fetchData();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        vacuna: item.vacuna,
        lote: item.lote,
        cantidad: item.cantidad,
        fecha_vencimiento: item.fecha_vencimiento,
        stock_minimo: item.stock_minimo,
        precio_unitario: item.precio_unitario,
        proveedor: item.proveedor || '',
        fecha_ingreso: item.fecha_ingreso,
        notas: item.notas || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        vacuna: '',
        lote: '',
        cantidad: 0,
        fecha_vencimiento: '',
        stock_minimo: 10,
        precio_unitario: 0,
        proveedor: '',
        fecha_ingreso: new Date().toISOString().split('T')[0],
        notas: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vacuna || !formData.lote || !formData.fecha_vencimiento) {
      toast.error('Vacuna, lote y fecha de vencimiento son requeridos');
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/inventario/${editingItem.id}`, {
          cantidad: formData.cantidad,
          stock_minimo: formData.stock_minimo,
          precio_unitario: formData.precio_unitario,
          proveedor: formData.proveedor,
          notas: formData.notas
        }, { withCredentials: true });
        toast.success('Inventario actualizado');
      } else {
        await axios.post(`${API}/inventario`, formData, { withCredentials: true });
        toast.success('Inventario agregado');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleViewHistory = async (item) => {
    setSelectedItem(item);
    try {
      const response = await axios.get(`${API}/inventario/${item.id}/movimientos`, {
        withCredentials: true
      });
      setMovimientos(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar el historial');
    }
  };

  const filteredInventario = inventario.filter(item => 
    item.vacuna.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.lote.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.proveedor && item.proveedor.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStockStatus = (item) => {
    if (item.cantidad <= 0) return { label: 'Agotado', color: 'bg-red-100 text-red-700' };
    if (item.cantidad <= item.stock_minimo) return { label: 'Stock Bajo', color: 'bg-amber-100 text-amber-700' };
    return { label: 'OK', color: 'bg-emerald-100 text-emerald-700' };
  };

  const getExpiryStatus = (fecha) => {
    const today = new Date();
    const expiry = new Date(fecha);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { label: 'Vencido', color: 'bg-red-100 text-red-700', days: diffDays };
    if (diffDays <= 30) return { label: `${diffDays} días`, color: 'bg-amber-100 text-amber-700', days: diffDays };
    if (diffDays <= 90) return { label: `${diffDays} días`, color: 'bg-blue-100 text-blue-700', days: diffDays };
    return { label: `${diffDays} días`, color: 'bg-slate-100 text-slate-600', days: diffDays };
  };

  const totalAlertas = alertas.resumen?.stock_bajo_count + alertas.resumen?.por_vencer_count + alertas.resumen?.vencidos_count || 0;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="inventario-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Inventario de Vacunas</h1>
            <p className="text-slate-500 mt-1">
              Control de stock y fechas de vencimiento
            </p>
          </div>
          <div className="flex gap-2">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportExcel}
              accept=".xlsx,.xls"
              className="hidden"
            />
            <Button 
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {importing ? 'Importando...' : 'Importar Excel'}
            </Button>
            <Button 
              variant="outline"
              onClick={fetchData}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
            <Button 
              className="btn-accent gap-2" 
              onClick={() => handleOpenDialog()}
              data-testid="add-inventario-btn"
            >
              <Plus className="w-4 h-4" />
              Agregar Stock
            </Button>
          </div>
        </div>

        {/* Alerts Banner */}
        {totalAlertas > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">
                    Hay {totalAlertas} alertas de inventario
                  </p>
                  <p className="text-sm text-amber-600">
                    {alertas.resumen?.stock_bajo_count > 0 && `${alertas.resumen.stock_bajo_count} con stock bajo • `}
                    {alertas.resumen?.por_vencer_count > 0 && `${alertas.resumen.por_vencer_count} por vencer • `}
                    {alertas.resumen?.vencidos_count > 0 && `${alertas.resumen.vencidos_count} vencidos`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {resumen.reduce((acc, r) => acc + r.cantidad_total, 0)}
                  </p>
                  <p className="text-sm text-slate-500">Unidades Totales</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {resumen.length}
                  </p>
                  <p className="text-sm text-slate-500">Tipos de Vacunas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {alertas.resumen?.stock_bajo_count || 0}
                  </p>
                  <p className="text-sm text-slate-500">Stock Bajo</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {(alertas.resumen?.por_vencer_count || 0) + (alertas.resumen?.vencidos_count || 0)}
                  </p>
                  <p className="text-sm text-slate-500">Por Vencer / Vencidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="inventario" className="w-full">
          <TabsList>
            <TabsTrigger value="inventario">Inventario</TabsTrigger>
            <TabsTrigger value="resumen">Resumen por Vacuna</TabsTrigger>
            <TabsTrigger value="alertas" className="relative">
              Alertas
              {totalAlertas > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0">
                  {totalAlertas}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inventario" className="mt-4">
            {/* Search */}
            <Card className="mb-4">
              <CardContent className="pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por vacuna, lote o proveedor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardContent className="pt-4">
                <div className="table-wrapper overflow-x-auto">
                  <table className="w-full">
                    <thead className="table-header">
                      <tr>
                        <th className="p-4 text-left">Vacuna</th>
                        <th className="p-4 text-left">Lote</th>
                        <th className="p-4 text-center">Cantidad</th>
                        <th className="p-4 text-center">Stock Mín.</th>
                        <th className="p-4 text-center">Estado</th>
                        <th className="p-4 text-left">Vencimiento</th>
                        <th className="p-4 text-right">Precio Unit.</th>
                        <th className="p-4 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center">
                            <div className="loading-spinner w-8 h-8 mx-auto"></div>
                          </td>
                        </tr>
                      ) : filteredInventario.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-slate-500">
                            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>No hay inventario registrado</p>
                            <Button 
                              variant="link" 
                              className="mt-2"
                              onClick={() => handleOpenDialog()}
                            >
                              Agregar el primero
                            </Button>
                          </td>
                        </tr>
                      ) : (
                        filteredInventario.map((item) => {
                          const stockStatus = getStockStatus(item);
                          const expiryStatus = getExpiryStatus(item.fecha_vencimiento);
                          
                          return (
                            <tr key={item.id} className="table-row" data-testid={`inv-row-${item.id}`}>
                              <td className="table-cell font-medium">{item.vacuna}</td>
                              <td className="table-cell font-mono text-sm">{item.lote}</td>
                              <td className="table-cell text-center font-bold">{item.cantidad}</td>
                              <td className="table-cell text-center text-slate-500">{item.stock_minimo}</td>
                              <td className="table-cell text-center">
                                <Badge className={stockStatus.color}>{stockStatus.label}</Badge>
                              </td>
                              <td className="table-cell">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm">{item.fecha_vencimiento}</span>
                                  <Badge className={expiryStatus.color}>{expiryStatus.label}</Badge>
                                </div>
                              </td>
                              <td className="table-cell text-right font-mono">
                                ${item.precio_unitario?.toLocaleString('es-CL') || 0}
                              </td>
                              <td className="table-cell text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenDialog(item)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewHistory(item)}
                                  >
                                    <History className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resumen" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Resumen por Tipo de Vacuna</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="table-wrapper overflow-x-auto">
                  <table className="w-full">
                    <thead className="table-header">
                      <tr>
                        <th className="p-4 text-left">Vacuna</th>
                        <th className="p-4 text-center">Cantidad Total</th>
                        <th className="p-4 text-center">Lotes</th>
                        <th className="p-4 text-right">Valor Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumen.map((r) => (
                        <tr key={r.vacuna} className="table-row">
                          <td className="table-cell font-medium">{r.vacuna}</td>
                          <td className="table-cell text-center font-bold">{r.cantidad_total}</td>
                          <td className="table-cell text-center">{r.lotes}</td>
                          <td className="table-cell text-right font-mono">
                            ${r.valor_total?.toLocaleString('es-CL') || 0}
                          </td>
                        </tr>
                      ))}
                      {resumen.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-500">
                            No hay datos de inventario
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alertas" className="mt-4 space-y-4">
            {/* Vencidos */}
            {alertas.vencidos?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Vacunas Vencidas ({alertas.vencidos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {alertas.vencidos.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{item.vacuna}</p>
                          <p className="text-sm text-red-600">Lote: {item.lote} • Venció: {item.fecha_vencimiento}</p>
                        </div>
                        <Badge className="bg-red-100 text-red-700">{item.cantidad} unidades</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Por vencer */}
            {alertas.por_vencer?.length > 0 && (
              <Card className="border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-amber-700 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Por Vencer (próximos 30 días) ({alertas.por_vencer.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {alertas.por_vencer.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium">{item.vacuna}</p>
                          <p className="text-sm text-amber-600">Lote: {item.lote} • Vence: {item.fecha_vencimiento}</p>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">{item.cantidad} unidades</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stock bajo */}
            {alertas.stock_bajo?.length > 0 && (
              <Card className="border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-blue-700 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    Stock Bajo ({alertas.stock_bajo.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {alertas.stock_bajo.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                          <p className="font-medium">{item.vacuna}</p>
                          <p className="text-sm text-blue-600">Lote: {item.lote} • Mínimo: {item.stock_minimo}</p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">{item.cantidad} unidades</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {totalAlertas === 0 && (
              <Card>
                <CardContent className="pt-6 text-center text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
                  <p className="text-emerald-600 font-medium">No hay alertas de inventario</p>
                  <p className="text-sm">Todo el stock está en orden</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Editar Stock' : 'Agregar Stock'}
              </DialogTitle>
              <DialogDescription>
                {editingItem 
                  ? 'Modifica la cantidad o configuración del lote' 
                  : 'Registra un nuevo lote de vacunas en el inventario'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vacuna">Vacuna *</Label>
                    <Select 
                      value={formData.vacuna} 
                      onValueChange={(value) => setFormData({...formData, vacuna: value})}
                      disabled={!!editingItem}
                    >
                      <SelectTrigger data-testid="select-vacuna">
                        <SelectValue placeholder="Seleccionar vacuna" />
                      </SelectTrigger>
                      <SelectContent>
                        {vacunas.map(v => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lote">Lote/Serie *</Label>
                    <Input
                      id="lote"
                      value={formData.lote}
                      onChange={(e) => setFormData({...formData, lote: e.target.value})}
                      placeholder="ABC123"
                      disabled={!!editingItem}
                      data-testid="input-lote"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cantidad">Cantidad *</Label>
                    <Input
                      id="cantidad"
                      type="number"
                      min="0"
                      value={formData.cantidad}
                      onChange={(e) => setFormData({...formData, cantidad: parseInt(e.target.value) || 0})}
                      data-testid="input-cantidad"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stock_minimo">Stock Mínimo</Label>
                    <Input
                      id="stock_minimo"
                      type="number"
                      min="0"
                      value={formData.stock_minimo}
                      onChange={(e) => setFormData({...formData, stock_minimo: parseInt(e.target.value) || 0})}
                      data-testid="input-stock-minimo"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fecha_vencimiento">Fecha Vencimiento *</Label>
                    <Input
                      id="fecha_vencimiento"
                      type="date"
                      value={formData.fecha_vencimiento}
                      onChange={(e) => setFormData({...formData, fecha_vencimiento: e.target.value})}
                      disabled={!!editingItem}
                      data-testid="input-fecha-vencimiento"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="precio_unitario">Precio Unitario</Label>
                    <Input
                      id="precio_unitario"
                      type="number"
                      min="0"
                      value={formData.precio_unitario}
                      onChange={(e) => setFormData({...formData, precio_unitario: parseFloat(e.target.value) || 0})}
                      data-testid="input-precio"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="proveedor">Proveedor</Label>
                  <Input
                    id="proveedor"
                    value={formData.proveedor}
                    onChange={(e) => setFormData({...formData, proveedor: e.target.value})}
                    placeholder="Nombre del proveedor"
                    data-testid="input-proveedor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notas">Notas</Label>
                  <Textarea
                    id="notas"
                    value={formData.notas}
                    onChange={(e) => setFormData({...formData, notas: e.target.value})}
                    placeholder="Notas adicionales..."
                    className="min-h-[60px]"
                    data-testid="input-notas"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="gap-2" data-testid="save-btn">
                  <Save className="w-4 h-4" />
                  {editingItem ? 'Actualizar' : 'Guardar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Historial de Movimientos</DialogTitle>
              <DialogDescription>
                {selectedItem?.vacuna} - Lote: {selectedItem?.lote}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto">
              {movimientos.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No hay movimientos registrados</p>
              ) : (
                <div className="space-y-3">
                  {movimientos.map((mov) => (
                    <div key={mov.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={
                          mov.tipo === 'ENTRADA' ? 'bg-emerald-100 text-emerald-700' :
                          mov.tipo === 'SALIDA' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {mov.tipo}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(mov.created_at).toLocaleString('es-CL')}
                        </span>
                      </div>
                      <p className="text-sm">
                        <span className="font-medium">{mov.cantidad_anterior}</span>
                        {' → '}
                        <span className="font-medium">{mov.cantidad_nueva}</span>
                        {' '}
                        <span className="text-slate-500">
                          ({mov.cantidad > 0 ? '+' : ''}{mov.cantidad})
                        </span>
                      </p>
                      {mov.motivo && (
                        <p className="text-xs text-slate-500 mt-1">{mov.motivo}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Import Result Dialog */}
        <Dialog open={importResult !== null} onOpenChange={() => setImportResult(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                Resultado de Importación
              </DialogTitle>
            </DialogHeader>
            {importResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{importResult.imported}</p>
                    <p className="text-sm text-green-700">Productos nuevos</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{importResult.updated}</p>
                    <p className="text-sm text-blue-700">Productos actualizados</p>
                  </div>
                </div>
                
                {importResult.errors?.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-700 mb-2">Errores:</p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {importResult.errors.map((err, i) => (
                        <li key={`${i}-${err}`}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setImportResult(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default InventarioPage;
